import type { BuildProgress } from "../domain/build/getProjectBuildGuide";
import type { SiteConditions } from "../domain/foundation/siteConditions";
import {
	type ClimateProfile,
	DEFAULT_INTL_CLIMATE_PROFILE,
	type LocationMode,
	type Market,
} from "../domain/location/types";
import {
	DEFAULT_SAUNA_ROTATION_DEG,
	type SaunaRotationDeg,
} from "../domain/viewer/saunaRotation";
import {
	DEFAULT_TIME_OF_DAY,
	type TimeOfDayValue,
} from "../domain/viewer/timeOfDay";
import {
	DEFAULT_FINISHES,
	type FinishConfig,
} from "../domain/finishes/types";
import type { InteriorLayout } from "../domain/interior/types";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
	TwoRoomConfig,
	VisibilityFlags,
} from "../engines/types";
import { resolveTwoRoomConfig } from "../engines/twoRoomSauna";
import { getT } from "../i18n";
import {
	DEFAULT_SITE_CONDITIONS,
	DEFAULT_THERMAL,
	DEFAULT_VIS,
} from "../pages/builder/constants";
import type { BuilderState } from "./useBuilderStore";

/**
 * zundo's `limit`. Once `pastStates` reaches it, `_handleSet` shifts the
 * OLDEST entry off, so a history at the limit no longer starts at the design's
 * real beginning. Exported so the history UI can tell "this is the starting
 * point" from "this is as far back as the buffer goes".
 */
export const BUILDER_HISTORY_LIMIT = 50;

export type PartializedBuilderState = Pick<
	BuilderState,
	| "market"
	| "locationMode"
	| "climateProfile"
	| "dimensions"
	| "partitionSplit"
		| "twoRoomConfig"
		| "timeOfDay"
		| "rotationDeg"
		| "wizardCapacityHint"
		| "visibility"
	| "openings"
	| "deckConfig"
	| "roofConfig"
	| "thermalInputs"
	| "siteConditions"
	| "interiorLayout"
	| "finishes"
	| "wallHeightM"
	| "indoorRoom"
	| "projectName"
	| "postalCode"
>;

export const INITIAL_BUILDER_STATE: BuilderState = {
	market: "se" satisfies Market,
	locationMode: "postal_code" satisfies LocationMode,
	climateProfile: DEFAULT_INTL_CLIMATE_PROFILE satisfies ClimateProfile,
	dimensions: { width: 2.5, length: 2 } satisfies SaunaDimensions,
	partitionSplit: 0.5,
		twoRoomConfig: resolveTwoRoomConfig() satisfies TwoRoomConfig,
		timeOfDay: DEFAULT_TIME_OF_DAY satisfies TimeOfDayValue,
		rotationDeg: DEFAULT_SAUNA_ROTATION_DEG satisfies SaunaRotationDeg,
		wizardCapacityHint: null,
	visibility: DEFAULT_VIS satisfies VisibilityFlags,
	openings: [],
	deckConfig: null satisfies DeckConfig | null,
	roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig satisfies RoofConfig,
	buildProgress: {} satisfies BuildProgress,
	thermalInputs: DEFAULT_THERMAL satisfies ThermalInputs,
	siteConditions: DEFAULT_SITE_CONDITIONS satisfies SiteConditions,
	interiorLayout: null satisfies InteriorLayout | null,
	finishes: { ...DEFAULT_FINISHES } satisfies FinishConfig,
	wallHeightM: null,
	indoorRoom: null,
	projectName: getT("se").rail_defaultProjectName,
	postalCode: "",
	saving: false,
	saveStatus: null,
	loadingDesign: false,
};

export function partializeBuilderState(
	state: BuilderState,
): PartializedBuilderState {
	return {
		dimensions: state.dimensions,
		partitionSplit: state.partitionSplit,
			twoRoomConfig: state.twoRoomConfig,
			timeOfDay: state.timeOfDay,
			rotationDeg: state.rotationDeg,
			wizardCapacityHint: state.wizardCapacityHint,
		visibility: state.visibility,
		openings: state.openings,
		deckConfig: state.deckConfig,
		roofConfig: state.roofConfig,
		thermalInputs: state.thermalInputs,
		siteConditions: state.siteConditions,
		interiorLayout: state.interiorLayout,
		finishes: state.finishes,
		wallHeightM: state.wallHeightM,
		indoorRoom: state.indoorRoom,
		projectName: state.projectName,
		market: state.market,
		locationMode: state.locationMode,
		climateProfile: state.climateProfile,
		postalCode: state.postalCode,
	};
}
